import React, { useCallback, useMemo } from 'react';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { ConfigureWriteBackLeverApiAuthMethod } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverApiAuthMethod';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { WriteBackToLeverAuthMethod } from 'libs/end-to-end/src/types/databases/workflows/strategies/lever-write-back';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';

export interface ConfigureWriteBackLeverAuthMethodProps
  extends ConfigureWorkflowBaseProps {
  authMethod: WriteBackToLeverAuthMethod | null;

  updateAuthMethod: (
    authMethod: WriteBackToLeverAuthMethod | null,
  ) => void;
}

export const ConfigureWriteBackLeverAuthMethod = ({
  authMethod,
  updateAuthMethod,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureWriteBackLeverAuthMethodProps) => {
  const setSelectedMethod = useCallback(
    (
      selected:
        | 'none'
        | WriteBackToLeverAuthMethod['type'],
    ) => {
      if (selected === 'none') {
        updateAuthMethod(null);
      } else if (selected !== authMethod?.type) {
        updateAuthMethod({
          type: selected,
        });
      }
    },
    [authMethod, updateAuthMethod],
  );

  const selectedMethod = useMemo(() => {
    return authMethod?.type ?? 'none';
  }, [authMethod]);

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold">Configure Lever Auth Method</h2>
      <p className="text-gray-600">
        Select and configure the authentication method for Lever.
      </p>
      <SelectBoxWithSearch
        type="single"
        list={[
          { optionLabel: 'None', optionValue: 'none' },
          { optionLabel: 'Merge', optionValue: 'merge' },
          { optionLabel: 'API Based', optionValue: 'api' },
        ]}
        label="Select an auth method"
        selected={selectedMethod}
        setSelected={setSelectedMethod}
      />

      {authMethod?.type === 'api' && (
        <ConfigureWriteBackLeverApiAuthMethod
          authMethod={authMethod}
          updateAuthMethod={updateAuthMethod}
          listSecretsPattern={listSecretsPattern}
          organizationId={organizationId}
          listDataSetsPattern={listDataSetsPattern}
        />
      )}

      {authMethod?.type === 'merge' && (
        <WorkflowDataSetSelector
          dataSetId={authMethod?.dataSetId ?? null}
          title="Select a merge ATS data set"
          listDataSetsPattern={listDataSetsPattern}
          setDataSetId={(dataSetId) => {
            updateAuthMethod({
              ...authMethod,
              dataSetId: dataSetId ?? undefined,
            });
          }}
          filterFn={isMergeAtsIntegrationDataSet}
        />
      )}
    </div>
  );
};
